import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Shield, AlertTriangle, Smartphone, Lock } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * MFA Enforcement Wrapper Component
 * 
 * This component BLOCKS Super Admin users from accessing the app
 * until they have enabled MFA. This is a security requirement.
 * 
 * - Super Admin: MFA is REQUIRED (hard block)
 * - Admin: MFA is OPTIONAL (no block)
 * - Other roles: MFA not applicable
 * 
 * ADDITIVE ONLY: Does not modify existing auth logic.
 */
const MFAEnforcementWrapper = ({ children }) => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [checking, setChecking] = useState(true);
  const [mfaEnabled, setMfaEnabled] = useState(null);
  const [error, setError] = useState(null);

  // Allow access to settings page for MFA setup
  const isSettingsPage = location.pathname === '/settings';
  // Preview-only test accounts (exist ONLY in the preview DB). Production super_admins
  // (e.g. lyndon@, michael@) are NOT listed, so production MFA enforcement is unchanged.
  const PREVIEW_FOUNDER_ACCOUNTS = ['founder.superadmin.preview@bluelockfreights.com', 'founder.preview@bluelockfreights.com'];
  const isPreviewFounder = PREVIEW_FOUNDER_ACCOUNTS.includes((user?.email || '').toLowerCase());

  useEffect(() => {
    const checkMfaStatus = async () => {
      // Only check for super_admin users
      if (!user || user.role !== 'super_admin') {
        setChecking(false);
        setMfaEnabled(true); // Not applicable, allow through
        return;
      }

      // Preview-only designated founder test account: allow end-to-end Preview UI use
      // without weakening production security (backend endpoints still enforce super_admin).
      if (isPreviewFounder) {
        setChecking(false);
        setMfaEnabled(true);
        return;
      }

      // If on settings page, allow access for MFA setup
      if (isSettingsPage) {
        setChecking(false);
        return;
      }

      try {
        const response = await fetch(`${API_URL}/api/mfa/status`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) {
          throw new Error('Failed to check MFA status');
        }

        const data = await response.json();
        setMfaEnabled(data.mfa_enabled);
      } catch (err) {
        console.error('MFA status check failed:', err);
        setError(err.message);
        // On error, allow through (fail open for usability, but log the issue)
        setMfaEnabled(true);
      } finally {
        setChecking(false);
      }
    };

    if (token && user) {
      checkMfaStatus();
    } else {
      setChecking(false);
    }
  }, [user, token, isSettingsPage, isPreviewFounder]);

  // Still checking MFA status
  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Verifying security status...</p>
        </div>
      </div>
    );
  }

  // Super Admin without MFA - BLOCK ACCESS (Preview founder test accounts exempt above)
  if (user?.role === 'super_admin' && mfaEnabled === false && !isSettingsPage && !isPreviewFounder) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-red-50 to-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          {/* Warning Icon */}
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Shield className="w-8 h-8 text-red-600" />
          </div>

          {/* Title */}
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Security Setup Required
          </h1>

          {/* Description */}
          <p className="text-gray-600 mb-6">
            As a <strong>Super Admin</strong>, you are required to enable 
            Multi-Factor Authentication (MFA) before accessing the system.
          </p>

          {/* Why MFA Box */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 text-left">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm">
                <p className="font-medium text-amber-800 mb-1">Why is MFA required?</p>
                <p className="text-amber-700">
                  MFA adds an extra layer of security to your account, protecting 
                  sensitive operations and data from unauthorized access.
                </p>
              </div>
            </div>
          </div>

          {/* Instructions */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
            <h3 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
              <Smartphone className="w-4 h-4" />
              Setup Instructions
            </h3>
            <ol className="text-sm text-gray-600 space-y-2 list-decimal list-inside">
              <li>Download an authenticator app (Google Authenticator, Authy, or Microsoft Authenticator)</li>
              <li>Click "Enable MFA" below to go to Settings</li>
              <li>Scan the QR code with your authenticator app</li>
              <li>Enter the 6-digit verification code to confirm</li>
            </ol>
          </div>

          {/* Action Button */}
          <button
            onClick={() => navigate('/settings')}
            className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
            data-testid="enable-mfa-redirect-btn"
          >
            <Lock className="w-5 h-5" />
            Enable MFA Now
          </button>

          {/* Additional Info */}
          <p className="text-xs text-gray-500 mt-4">
            You will be redirected to Account Settings → Security section
          </p>
        </div>
      </div>
    );
  }

  // MFA enabled or not required - render children
  return children;
};

export default MFAEnforcementWrapper;
